import * as productOrderApi from '@apis/product-orders';
import { RefundScheduleLog, RefundScheduleLogsQuery } from '@apis/product-orders';
import { useQuery } from '@tanstack/react-query';
import { Button, Card, Col, DatePicker, Input, Row, Select, Space, Table, Tag, Typography } from 'antd';
import { FilterSelect } from '@components/common/FilterSelect';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import React, { useState } from 'react';
import { toast } from 'react-toastify';

const { Text } = Typography;
const { RangePicker } = DatePicker;

const ProductOrdersRefundSchedulePage: React.FC = () => {
  const [filters, setFilters] = useState<RefundScheduleLogsQuery>({
    page: '0',
    size: '20',
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // 환불 스케줄 로그 조회
  const {
    data: refundScheduleLogsData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['refund-schedule-logs', filters],
    queryFn: async () => {
      const response = await productOrderApi.searchRefundScheduleLogs(filters);
      return response.data;
    },
  });

  // 필터 변경 핸들러
  const handleFilterChange = (key: keyof RefundScheduleLogsQuery, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      page: '0', // 필터 변경 시 첫 페이지로 이동
    }));
    setCurrentPage(1);
  };

  // 날짜 범위 변경 핸들러
  const handleDateRangeChange = (dates: any) => {
    if (dates && dates.length === 2) {
      setFilters((prev) => ({
        ...prev,
        startDate: dates[0].format('YYYY-MM-DD'),
        endDate: dates[1].format('YYYY-MM-DD'),
        page: '0',
      }));
      setCurrentPage(1);
    } else {
      setFilters((prev) => ({
        ...prev,
        startDate: undefined,
        endDate: undefined,
        page: '0',
      }));
      setCurrentPage(1);
    }
  };

  // 필터 초기화
  const handleResetFilters = () => {
    setFilters({
      page: '0',
      size: '20',
    });
    setCurrentPage(1);
  };

  // 페이지 변경 핸들러
  const handlePageChange = (page: number, size?: number) => {
    const newPageSize = size || pageSize;
    setCurrentPage(page);
    setPageSize(newPageSize);
    setFilters((prev) => ({
      ...prev,
      page: (page - 1).toString(),
      size: newPageSize.toString(),
    }));
  };

  // 상태에 따른 색상 반환
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success':
        return 'green';
      case 'failed':
        return 'red';
      default:
        return 'default';
    }
  };

  // 채널에 따른 색상 반환
  const getChannelColor = (channel: string) => {
    switch (channel) {
      case 'app':
        return 'blue';
      case 'naver-store':
        return 'green';
      case 'kakao-reservation':
        return 'orange';
      default:
        return 'default';
    }
  };

  // 채널명 변환
  const getChannelName = (channel: string) => {
    switch (channel) {
      case 'app':
        return '앱';
      case 'naver-store':
        return '네이버 스토어';
      case 'kakao-reservation':
        return '카카오 예약';
      default:
        return channel;
    }
  };

  // 테이블 컬럼 정의
  const columns: ColumnsType<RefundScheduleLog> = [
    {
      title: 'ID',
      dataIndex: '_id',
      key: '_id',
      render: (id: string) => (
        <Button
          size="small"
          onClick={() => {
            navigator.clipboard.writeText(id);
            toast.dark('ID가 복사되었습니다.', {
              position: 'top-center',
              autoClose: 2000,
            });
          }}>
          ID 복사
        </Button>
      ),
      width: 100,
    },
    {
      title: '상태',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => <Tag color={getStatusColor(status)}>{status === 'success' ? '성공' : '실패'}</Tag>,
      width: 80,
    },
    {
      title: '채널',
      dataIndex: 'channel',
      key: 'channel',
      render: (channel: string) => <Tag color={getChannelColor(channel)}>{getChannelName(channel)}</Tag>,
      width: 120,
    },
    {
      title: '주문 ID',
      dataIndex: 'orderId',
      key: 'orderId',
      render: (orderId: string) => <Text style={{ fontSize: '12px' }}>{orderId}</Text>,
      width: 150,
    },
    {
      title: '구매자명',
      dataIndex: ['orderInfo', 'buyerName'],
      key: 'buyerName',
      render: (buyerName: string) => buyerName || '-',
      width: 100,
    },
    {
      title: '구매자 전화번호',
      dataIndex: ['orderInfo', 'buyerPhone'],
      key: 'buyerPhone',
      render: (buyerPhone: string) => buyerPhone || '-',
      width: 130,
    },
    {
      title: '상품명',
      dataIndex: ['orderInfo', 'productName'],
      key: 'productName',
      render: (productName: string) => (
        <Text style={{ fontSize: '12px' }} ellipsis={{ tooltip: productName }}>
          {productName || '-'}
        </Text>
      ),
      width: 200,
    },
    {
      title: '환불 금액',
      dataIndex: 'refundAmount',
      key: 'refundAmount',
      render: (amount: number) => (amount ? `${amount.toLocaleString()}원` : '-'),
      width: 120,
    },
    {
      title: '실패 사유',
      dataIndex: 'failureReason',
      key: 'failureReason',
      render: (reason: string) =>
        reason ? (
          <Text style={{ fontSize: '12px', color: '#ff4d4f' }} ellipsis={{ tooltip: reason }}>
            {reason}
          </Text>
        ) : (
          '-'
        ),
      width: 200,
    },
    {
      title: '처리 시간',
      dataIndex: 'processedAt',
      key: 'processedAt',
      render: (processedAt: string) => dayjs(processedAt).format('YYYY-MM-DD HH:mm:ss'),
      width: 150,
    },
    {
      title: '생성 시간',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (createdAt: string) => dayjs(createdAt).format('YYYY-MM-DD HH:mm:ss'),
      width: 150,
    },
  ];

  return (
    <div className="dashboard" style={{ position: 'relative' }}>
      <div className="page-header">
        <div>환불 스케줄 내역</div>
        <Button onClick={() => refetch()} loading={isLoading}>
          새로고침
        </Button>
      </div>
      <div className="page-content" style={{ padding: '24px', overflow: 'auto' }}>
        {/* 필터 섹션 */}
        <Card title="필터" style={{ marginBottom: '24px' }}>
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} sm={8} md={6}>
              <Text strong>상태:</Text>
              <FilterSelect
                style={{ width: '100%', marginTop: '4px' }}
                placeholder="상태 선택"
                allowClear
                value={filters.status}
                onChange={(value) => handleFilterChange('status', value)}
                options={[
                  { label: '성공', value: 'success' },
                  { label: '실패', value: 'failed' },
                ]}
              />
            </Col>
            <Col xs={24} sm={8} md={6}>
              <Text strong>채널:</Text>
              <FilterSelect
                style={{ width: '100%', marginTop: '4px' }}
                placeholder="채널 선택"
                allowClear
                value={filters.channel}
                onChange={(value) => handleFilterChange('channel', value)}
                options={[
                  { label: '앱', value: 'app' },
                  { label: '네이버 스토어', value: 'naver-store' },
                  { label: '카카오 예약', value: 'kakao-reservation' },
                ]}
              />
            </Col>
            <Col xs={24} sm={8} md={6}>
              <Text strong>구매자명:</Text>
              <Input
                style={{ marginTop: '4px' }}
                placeholder="구매자명 입력"
                value={filters.buyerName}
                onChange={(e) => handleFilterChange('buyerName', e.target.value)}
              />
            </Col>
            <Col xs={24} sm={8} md={6}>
              <Text strong>구매자 전화번호:</Text>
              <Input
                style={{ marginTop: '4px' }}
                placeholder="전화번호 입력"
                value={filters.buyerPhone}
                onChange={(e) => handleFilterChange('buyerPhone', e.target.value)}
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Text strong>날짜 범위:</Text>
              <RangePicker
                style={{ width: '100%', marginTop: '4px' }}
                value={filters.startDate && filters.endDate ? [dayjs(filters.startDate), dayjs(filters.endDate)] : null}
                onChange={handleDateRangeChange}
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Text strong>표시 개수:</Text>
              <Select
                style={{ width: '100%', marginTop: '4px' }}
                value={pageSize}
                onChange={(value) => {
                  setPageSize(value);
                  handleFilterChange('size', value.toString());
                }}
                options={[
                  { label: '20개', value: 20 },
                  { label: '50개', value: 50 },
                  { label: '100개', value: 100 },
                ]}
              />
            </Col>
            <Col xs={24} sm={24} md={8}>
              <Space>
                <Button
                  onClick={handleResetFilters}
                  disabled={
                    !filters.status &&
                    !filters.channel &&
                    !filters.buyerName &&
                    !filters.buyerPhone &&
                    !filters.startDate
                  }>
                  필터 초기화
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>

        {/* 통계 정보 */}
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col xs={24} sm={8}>
            <Card>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1890ff' }}>
                  {refundScheduleLogsData?.total || 0}
                </div>
                <div style={{ color: '#666' }}>전체 로그</div>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#52c41a' }}>
                  {refundScheduleLogsData?.summary?.successCount || 0}
                </div>
                <div style={{ color: '#666' }}>성공</div>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#ff4d4f' }}>
                  {refundScheduleLogsData?.summary?.failureCount || 0}
                </div>
                <div style={{ color: '#666' }}>실패</div>
              </div>
            </Card>
          </Col>
        </Row>

        {/* 환불 스케줄 로그 테이블 */}
        <Card title="환불 스케줄 로그">
          <Table
            columns={columns}
            dataSource={refundScheduleLogsData?.contents || []}
            rowKey="_id"
            loading={isLoading}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: refundScheduleLogsData?.total || 0,
              onChange: handlePageChange,
              onShowSizeChange: handlePageChange,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} / ${total}개`,
              pageSizeOptions: ['20', '50', '100'],
              size: 'default',
            }}
            size="small"
            scroll={{ x: 1200 }}
          />
        </Card>
      </div>
    </div>
  );
};

export default ProductOrdersRefundSchedulePage;
